/**
 * Credential Manager page — Admin only.
 *
 * Displays credential entries for all integrations with masked values.
 * Update form per credential. Test Connection buttons with feedback.
 * Last test date display.
 *
 * Requirements: 49.2, 49.3, 49.5, 49.9
 */

import { useState, useEffect, useCallback } from 'react';
import {
  adminService,
  CredentialEntry,
  TestConnectionResult,
} from '../../services/adminService';

export function CredentialManager() {
  const [credentials, setCredentials] = useState<CredentialEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});
  const [testResults, setTestResults] = useState<Record<string, TestConnectionResult>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getCredentials();
      setCredentials(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credentials.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const startEdit = (cred: CredentialEntry) => {
    setEditingId(cred.integrationId);
    const fields: Record<string, string> = {};
    cred.fields.forEach((f) => { fields[f.fieldName] = ''; });
    setEditFields(fields);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFields({});
  };

  const saveCredentials = async (integrationId: string) => {
    try {
      setSaving(true);
      // Only send fields that have values
      const nonEmpty = Object.fromEntries(
        Object.entries(editFields).filter(([, v]) => v.length > 0),
      );
      if (Object.keys(nonEmpty).length === 0) {
        setError('Please enter at least one credential value.');
        return;
      }
      await adminService.updateCredentials(integrationId, nonEmpty);
      setEditingId(null);
      await loadCredentials();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update credentials.');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (integrationId: string) => {
    try {
      setTesting(integrationId);
      const result = await adminService.testCredentialConnection(integrationId);
      setTestResults((prev) => ({ ...prev, [integrationId]: result }));
      await loadCredentials();
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [integrationId]: {
          integrationId,
          success: false,
          message: err instanceof Error ? err.message : 'Test failed.',
          testedAt: new Date().toISOString(),
        },
      }));
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div >
        <h1 className="text-lg font-semibold text-text-primary mb-6">Credential Manager</h1>
        <p className="text-slate-400">Loading credentials...</p>
      </div>
    );
  }

  return (
    <div className=" max-w-5xl">
      <h1 className="text-lg font-semibold text-text-primary mb-2">Credential Manager</h1>
      <p className="text-sm text-slate-500 mb-6">
        Manage API credentials for all integrations. Values are masked for security.
        Updates are recorded in the audit log (values are never logged).
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-red-700 text-sm mb-5" role="alert">
          {error}
          <button className="ml-4 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="space-y-4">
        {credentials.map((cred) => (
          <div key={cred.integrationId} className="bg-white rounded-lg border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-medium text-text-primary">{cred.integrationName}</h2>
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                  cred.status === 'Configured' ? 'bg-green-100 text-green-700' :
                  cred.status === 'Partial' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {cred.status} ({cred.configuredCount}/{cred.totalFields})
                </span>
              </div>
              <div className="flex items-center gap-3">
                {cred.lastTestDate && (
                  <span className={`text-sm ${cred.lastTestResult === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    Last test: {new Date(cred.lastTestDate).toLocaleDateString()}
                    {' '}({cred.lastTestResult})
                  </span>
                )}
                <button
                  className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
                  onClick={() => testConnection(cred.integrationId)}
                  disabled={testing === cred.integrationId}
                >
                  {testing === cred.integrationId ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </div>

            {/* Test result feedback */}
            {testResults[cred.integrationId] && (
              <div
                className={`text-sm px-3 py-2 rounded mb-3 ${
                  testResults[cred.integrationId].success
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {testResults[cred.integrationId].message}
              </div>
            )}

            {/* Credential fields */}
            <div className="space-y-2">
              {cred.fields.map((field) => (
                <div key={field.fieldName} className="flex items-center gap-3">
                  <span className="text-sm text-slate-500 w-48 truncate" title={field.fieldName}>
                    {field.fieldName}
                  </span>
                  {editingId === cred.integrationId ? (
                    <input
                      type="password"
                      placeholder={field.maskedValue}
                      value={editFields[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setEditFields((prev) => ({ ...prev, [field.fieldName]: e.target.value }))
                      }
                      className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 flex-1"
                      aria-label={`Update ${field.fieldName}`}
                    />
                  ) : (
                    <span className="font-mono text-sm text-gray-500">
                      {field.maskedValue}
                    </span>
                  )}
                  {!field.hasValue && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full">Not set</span>
                  )}
                </div>
              ))}
            </div>

            {/* Edit controls */}
            <div className="mt-3 flex gap-2">
              {editingId === cred.integrationId ? (
                <>
                  <button
                    className="px-4 py-2 bg-baker-red text-white rounded-lg text-sm font-medium hover:bg-baker-red/90"
                    onClick={() => saveCredentials(cred.integrationId)}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 hover:bg-page"
                  onClick={() => startEdit(cred)}
                >
                  Update Credentials
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
